import usePageMetadata, { PageMetadata } from '@hooks/usePageMetadata'
import {
  FunctionComponent,
  MutableRefObject,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled, { css } from 'styled-components'
import AppNexus from '@components/AppNexus'
import { layout } from '@measures/responsive'
import { ViewportTypeValues } from '@contexts/viewport'
import useMount from '@hooks/useMount'
import contextBlickTV from '@contexts/blickTV'
import { printCSS } from '@utils/style'

type ValidWelcomeAd = Required<PageMetadata>['welcomeAd']

const {
  desktop: { minWidth: desktopMinWidth },
} = layout

const isValidWelcomeAd = (welcomeAd: any): welcomeAd is ValidWelcomeAd =>
  welcomeAd?.isAdDisabled === false

const { BlickTVContext } = contextBlickTV

const HEADER_OFFSET_DESKTOP = '60px'
const HEADER_OFFSET_MOBILE = '48px'
const headerOffsetIntDesktop = parseInt(HEADER_OFFSET_DESKTOP, 10)
const headerOffsetIntMobile = parseInt(HEADER_OFFSET_MOBILE, 10)
const getHeaderOffset = () =>
  window.innerWidth < desktopMinWidth
    ? headerOffsetIntMobile
    : headerOffsetIntDesktop
let ticking = false

const Wrapper = styled.div<{ isVisible: boolean }>`
  ${({ isVisible, theme: { viewportType } }) => {
    return css`
      display: ${!isVisible && 'none'};
      position: relative;
      width: 100vw;
      height: ${viewportType === ViewportTypeValues.DESKTOP
        ? `calc(100vh - ${HEADER_OFFSET_DESKTOP})`
        : `calc(100vh - ${HEADER_OFFSET_MOBILE})`};
      ${printCSS(css`
        display: none;
      `)}
    `
  }};
`

const AdWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const setOpacity = ({
  elementRef,
  adWrapperRef,
  currentOpacity,
}: {
  elementRef: RefObject<HTMLDivElement>
  adWrapperRef: RefObject<HTMLDivElement>
  currentOpacity: MutableRefObject<number>
}) => {
  //! Do not calculate or set opacity when already 0 and scrolled to the main content
  if (window.scrollY >= window.innerHeight - getHeaderOffset()) {
    if (currentOpacity.current !== 0) {
      if (elementRef?.current) {
        currentOpacity.current = 0
        elementRef.current.style.opacity = '0'
        if (adWrapperRef.current) {
          adWrapperRef.current.style.visibility = 'hidden'
        }
      }
    }
  } else {
    const newOpacity =
      Math.round(
        (1 -
          window.scrollY / (window.innerHeight - getHeaderOffset()) +
          Number.EPSILON) *
          100
      ) / 100

    if (Math.abs(newOpacity - currentOpacity.current) >= 0.01) {
      if (elementRef?.current) {
        if (currentOpacity.current === 0) {
          if (adWrapperRef.current) {
            adWrapperRef.current.style.visibility = ''
          }
        }
        currentOpacity.current = newOpacity
        elementRef.current.style.opacity = `${newOpacity}`
      }
    }
  }
}

const allowedToPopOutBlickTV = () =>
  window.scrollY >= window.innerHeight - getHeaderOffset()

const WelcomeAd: FunctionComponent = () => {
  const { welcomeAd } = usePageMetadata()

  const { setAllowPopOutFunction: setAllowBlickTVPopOutFunction } =
    useContext(BlickTVContext)
  const [isVisible, setIsVisible] = useState(false)
  const [isClosed, setIsClosed] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const adWrapperRef = useRef<HTMLDivElement>(null)
  const currentOpacity = useRef<number>(1)
  const isMounted = useMount()

  const onScroll = useCallback(() => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        setOpacity({
          elementRef: wrapperRef,
          adWrapperRef,
          currentOpacity,
        })
        ticking = false
      })
      ticking = true
    }
  }, [wrapperRef])

  useEffect(() => {
    window.Ads.helpers.setWelcomeAd = () => {
      if (isMounted()) {
        setAllowBlickTVPopOutFunction(allowedToPopOutBlickTV)
        setIsVisible(true)
      }
    }
    window.Ads.helpers.closeWelcomeAd = () => {
      setIsClosed(true)
    }
  }, [isMounted, setAllowBlickTVPopOutFunction])

  useEffect(() => {
    if (isVisible) {
      window.removeEventListener('scroll', onScroll)
      window.addEventListener('scroll', onScroll)
      onScroll()
    } else {
      window.removeEventListener('scroll', onScroll)
    }
  }, [isVisible, onScroll])

  useEffect(() => {
    return () => {
      window.removeEventListener('scroll', onScroll)
      setAllowBlickTVPopOutFunction(() => true)
    }
  }, [setAllowBlickTVPopOutFunction, onScroll])

  if (!isValidWelcomeAd(welcomeAd) || isClosed) {
    return null
  }

  return (
    <Wrapper isVisible={isVisible} ref={wrapperRef}>
      <AdWrapper ref={adWrapperRef}>
        <AppNexus {...welcomeAd} />
      </AdWrapper>
    </Wrapper>
  )
}

export default WelcomeAd
